import React, { useState, useEffect } from "react";
import { Palette, Save, CheckCircle2, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const BrandingPage: React.FC = () => {
  const [branding, setBranding] = useState({
    institution_name: "Corvenio ERP University",
    tagline: "Enterprise Higher Education Management Platform",
    primary_color: "#4f46e5",
    secondary_color: "#0284c7",
    accent_color: "#10b981",
    logo_url: "/assets/logo.png",
    dark_theme: true,
    custom_css: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("branding");
      if (res.data) {
        setBranding((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load branding configuration.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("branding", branding);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save branding settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading institutional branding configuration...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-6">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-indigo-400" />
          <div>
            <h2 className="text-base font-bold text-slate-100">Institutional Branding & Logo Assets</h2>
            <p className="text-slate-400 text-[11px]">Primary logos, favicons, custom CSS, and brand palette tokens.</p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Branding Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Branding</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Institution Display Name</label>
          <input
            type="text"
            value={branding.institution_name}
            onChange={(e) => {
              setSaved(false);
              setBranding({ ...branding, institution_name: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Portal Tagline / Subtitle</label>
          <input
            type="text"
            value={branding.tagline}
            onChange={(e) => {
              setSaved(false);
              setBranding({ ...branding, tagline: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Primary Color (Hex)</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.primary_color}
              onChange={(e) => {
                setSaved(false);
                setBranding({ ...branding, primary_color: e.target.value });
              }}
              className="w-8 h-8 rounded border border-slate-700 bg-slate-950 cursor-pointer"
            />
            <input
              type="text"
              value={branding.primary_color}
              onChange={(e) => {
                setSaved(false);
                setBranding({ ...branding, primary_color: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-200 text-xs font-mono font-bold"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Secondary Color (Hex)</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.secondary_color}
              onChange={(e) => {
                setSaved(false);
                setBranding({ ...branding, secondary_color: e.target.value });
              }}
              className="w-8 h-8 rounded border border-slate-700 bg-slate-950 cursor-pointer"
            />
            <input
              type="text"
              value={branding.secondary_color}
              onChange={(e) => {
                setSaved(false);
                setBranding({ ...branding, secondary_color: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-200 text-xs font-mono font-bold"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">Accent Color (Hex)</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.accent_color}
              onChange={(e) => {
                setSaved(false);
                setBranding({ ...branding, accent_color: e.target.value });
              }}
              className="w-8 h-8 rounded border border-slate-700 bg-slate-950 cursor-pointer"
            />
            <input
              type="text"
              value={branding.accent_color}
              onChange={(e) => {
                setSaved(false);
                setBranding({ ...branding, accent_color: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-200 text-xs font-mono font-bold"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-slate-300 mb-1">Custom CSS Overrides</label>
        <textarea
          rows={4}
          value={branding.custom_css}
          onChange={(e) => {
            setSaved(false);
            setBranding({ ...branding, custom_css: e.target.value });
          }}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-slate-200 text-xs font-mono"
        />
      </div>
    </div>
  );
};
